import React, { useState, useRef, useEffect, useCallback } from "react";
import { MessageSquareText, X, Send, Bot, Volume2, VolumeX } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { sendChat, voiceTranscribe, synthesizeSpeech } from "../lib/api";
import VoiceButton from "./VoiceButton";
import { voiceStr } from "../data/voiceStrings";

const RTL = ["ar", "fa", "ckb"];

// Minimal chrome strings per language (assistant replies come localized from the LLM).
const UI = {
  de: { title: "Airport Taxi Assistent", sub: "Wir antworten sofort", ph: "Ihre Frage …", greet: "Grüezi! 👋 Wie kann ich Ihnen bei Ihrem Flughafentransfer helfen?", err: "Entschuldigung, es gab ein Problem. Bitte versuchen Sie es erneut oder rufen Sie uns an." },
  en: { title: "Airport Taxi Assistant", sub: "We reply instantly", ph: "Your question …", greet: "Hello! 👋 How can I help with your airport transfer?", err: "Sorry, something went wrong. Please try again or call us." },
  fr: { title: "Assistant Airport Taxi", sub: "Réponse immédiate", ph: "Votre question …", greet: "Bonjour ! 👋 Comment puis-je vous aider pour votre transfert aéroport ?", err: "Désolé, une erreur est survenue. Réessayez ou appelez-nous." },
  it: { title: "Assistente Airport Taxi", sub: "Rispondiamo subito", ph: "La tua domanda …", greet: "Ciao! 👋 Come posso aiutarti con il tuo transfer aeroportuale?", err: "Spiacenti, si è verificato un problema. Riprova o chiamaci." },
  es: { title: "Asistente Airport Taxi", sub: "Respondemos al instante", ph: "Tu pregunta …", greet: "¡Hola! 👋 ¿Cómo puedo ayudarte con tu traslado al aeropuerto?", err: "Lo sentimos, hubo un problema. Inténtalo de nuevo o llámanos." },
  pt: { title: "Assistente Airport Taxi", sub: "Respondemos na hora", ph: "A sua pergunta …", greet: "Olá! 👋 Como posso ajudar com o seu transfer para o aeroporto?", err: "Desculpe, ocorreu um problema. Tente novamente ou ligue-nos." },
  tr: { title: "Airport Taxi Asistanı", sub: "Anında yanıt", ph: "Sorunuz …", greet: "Merhaba! 👋 Havalimanı transferinizde size nasıl yardımcı olabilirim?", err: "Üzgünüz, bir sorun oluştu. Tekrar deneyin veya bizi arayın." },
  ru: { title: "Ассистент Airport Taxi", sub: "Отвечаем мгновенно", ph: "Ваш вопрос …", greet: "Здравствуйте! 👋 Чем могу помочь с трансфером в аэропорт?", err: "Извините, произошла ошибка. Попробуйте снова или позвоните нам." },
  ar: { title: "مساعد Airport Taxi", sub: "نرد فوراً", ph: "سؤالك …", greet: "مرحباً! 👋 كيف يمكنني مساعدتك في نقلك من وإلى المطار؟", err: "عذراً، حدثت مشكلة. حاول مرة أخرى أو اتصل بنا." },
  fa: { title: "دستیار Airport Taxi", sub: "فوری پاسخ می‌دهیم", ph: "سؤال شما …", greet: "سلام! 👋 چطور می‌توانم در ترانسفر فرودگاه به شما کمک کنم؟", err: "متأسفیم، مشکلی پیش آمد. دوباره تلاش کنید یا تماس بگیرید." },
  hi: { title: "Airport Taxi सहायक", sub: "तुरंत उत्तर", ph: "आपका प्रश्न …", greet: "नमस्ते! 👋 मैं आपके एयरपोर्ट ट्रांसफर में कैसे मदद कर सकता हूँ?", err: "क्षमा करें, कोई समस्या हुई। पुनः प्रयास करें या हमें कॉल करें।" },
  zh: { title: "Airport Taxi 助手", sub: "即时回复", ph: "您的问题 …", greet: "您好！👋 关于您的机场接送，我能帮您什么？", err: "抱歉，出现问题。请重试或致电我们。" },
  ko: { title: "Airport Taxi 어시스턴트", sub: "즉시 답변", ph: "질문을 입력하세요 …", greet: "안녕하세요! 👋 공항 트랜스퍼에 대해 무엇을 도와드릴까요?", err: "죄송합니다. 문제가 발생했습니다. 다시 시도하거나 전화해 주세요." },
  ckb: { title: "یاریدەدەری Airport Taxi", sub: "دەستبەجێ وەڵام دەدەینەوە", ph: "پرسیارەکەت …", greet: "بەخێربێیت! 👋 چۆن دەتوانم لە گواستنەوەی فڕۆکەخانەدا یارمەتیت بدەم؟", err: "ببورە، کێشەیەک ڕوویدا. دووبارە هەوڵبدەرەوە یان پەیوەندیمان پێوە بکە." },
};

function uid() {
  return "cs_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function ChatWidget() {
  const { lang } = useLang();
  const ui = UI[lang] || UI.en;
  const rtl = RTL.includes(lang);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const [speak, setSpeak] = useState(() => localStorage.getItem("atz_chat_tts") === "1");
  const [speakingIdx, setSpeakingIdx] = useState(-1);

  const toggleSpeak = () => {
    setSpeak((s) => {
      const nv = !s;
      localStorage.setItem("atz_chat_tts", nv ? "1" : "0");
      if (!nv && audioRef.current) { try { audioRef.current.pause(); } catch { /* ignore */ } audioRef.current = null; setSpeakingIdx(-1); }
      return nv;
    });
  };

  const playTts = useCallback(async (text, idx = -1) => {
    if (!text) return;
    try {
      if (audioRef.current) { try { audioRef.current.pause(); } catch { /* ignore */ } audioRef.current = null; }
      const url = await synthesizeSpeech(text, lang);
      const a = new Audio(url);
      audioRef.current = a;
      setSpeakingIdx(idx);
      a.onended = () => { URL.revokeObjectURL(url); setSpeakingIdx(-1); if (audioRef.current === a) audioRef.current = null; };
      await a.play();
    } catch {
      setSpeakingIdx(-1);
    }
  }, [lang]);

  if (!sessionRef.current) {
    let sid = localStorage.getItem("atz_chat_sid");
    if (!sid) {
      sid = uid();
      localStorage.setItem("atz_chat_sid", sid);
    }
    sessionRef.current = sid;
  }

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: ui.greet }]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = useCallback(async (override, forceSpeak = false) => {
    const text = (typeof override === "string" ? override : input).trim();
    if (!text || loading) return;
    if (typeof override !== "string") setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { reply } = await sendChat(sessionRef.current, text, lang);
      setMessages((m) => {
        const next = [...m, { role: "assistant", text: reply }];
        if (speak || forceSpeak) playTts(reply, next.length - 1);
        return next;
      });
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: ui.err }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, lang, ui.err, speak, playTts]);

  const vstr = voiceStr(lang);
  const handleVoice = useCallback(async (blob) => {
    try {
      const { text } = await voiceTranscribe(blob, lang);
      if (text && text.trim()) await send(text.trim(), true);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: ui.err }]);
    }
  }, [lang, send, ui.err]);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={ui.title}
          data-testid="chat-open-button"
          className="fixed bottom-3 right-5 z-50 flex items-center gap-2 rounded-full bg-[#F5B400] text-neutral-900 pl-4 pr-5 h-14 shadow-xl shadow-black/25 hover:scale-105 transition-transform font-semibold"
        >
          <MessageSquareText className="w-6 h-6" />
          <span className="text-sm font-bold">AI</span>
        </button>
      )}

      {open && (
        <div
          dir={rtl ? "rtl" : "ltr"}
          data-testid="chat-panel"
          className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-[380px] h-[70vh] max-h-[560px] flex flex-col bg-white rounded-2xl shadow-2xl shadow-black/30 overflow-hidden border border-black/5"
        >
          <div className="flex items-center gap-3 bg-neutral-900 text-white px-4 py-3">
            <div className="w-9 h-9 rounded-full bg-[#F5B400] flex items-center justify-center text-neutral-900">
              <Bot className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm leading-tight truncate">{ui.title}</div>
              <div className="text-[11px] text-emerald-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> {ui.sub}
              </div>
            </div>
            <button onClick={toggleSpeak} aria-label="Voice reply" title={vstr.speak} data-testid="chat-tts-toggle" data-tts-on={speak ? "1" : "0"} className={`mr-1 rtl:mr-0 rtl:ml-1 transition-colors ${speak ? "text-[#F5B400]" : "text-white/60 hover:text-white"}`}>
              {speak ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            <button onClick={() => setOpen(false)} aria-label="Close" data-testid="chat-close-button" className="text-white/70 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-neutral-50" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`flex flex-col max-w-[82%] ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <div
                    className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#F5B400] text-neutral-900 rounded-br-sm"
                        : "bg-white text-neutral-800 border border-black/5 rounded-bl-sm shadow-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                  {m.role === "assistant" && (
                    <button
                      onClick={() => playTts(m.text, i)}
                      aria-label={vstr.speak}
                      data-testid={`chat-play-${i}`}
                      className={`mt-1 inline-flex items-center gap-1 text-[11px] transition-colors ${speakingIdx === i ? "text-[#D99A00]" : "text-neutral-400 hover:text-neutral-700"}`}
                    >
                      <Volume2 className={`w-3.5 h-3.5 ${speakingIdx === i ? "animate-pulse" : ""}`} /> {vstr.speak}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-black/5 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <span className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-neutral-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 rounded-full bg-neutral-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 rounded-full bg-neutral-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-end gap-2 p-3 border-t border-black/5 bg-white">
            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={ui.ph}
              data-testid="chat-input"
              className="flex-1 resize-none max-h-24 rounded-xl border border-neutral-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#F5B400] focus:ring-1 focus:ring-[#F5B400]"
            />
            <VoiceButton onAudio={handleVoice} str={vstr} variant="icon" data-testid="chat-voice-button" />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              aria-label="Send"
              data-testid="chat-send-button"
              className="w-11 h-11 shrink-0 rounded-xl bg-[#F5B400] text-neutral-900 flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
